import * as THREE from 'three'
import { SogsGpuPacker } from '@/plugins/gsplat/gpu/SogsGpuPacker'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('gsplat:core')

/**
 * SOGS 元数据结构
 */
export interface SogsMeta {
  /** 格式版本，1 为 min-max 线性量化，2 为 codebook 量化 */
  version: 1 | 2
  /** 位置量化参数，含 mins/maxs 范围 */
  means: {
    mins: [number, number, number]
    maxs: [number, number, number]
  }
  /** 缩放量化参数，V1 使用 mins/maxs，V2 使用 codebook */
  scales: {
    mins?: [number, number, number]
    maxs?: [number, number, number]
    codebook?: Float32Array
  }
  /** SH DC 分量量化参数，V1 使用 mins/maxs，V2 使用 codebook */
  sh0: {
    mins?: [number, number, number, number]
    maxs?: [number, number, number, number]
    codebook?: Float32Array
  }
  /** SH 高阶系数元信息，无高阶系数时省略 */
  shN?: {
    count?: number
    mins?: number
    maxs?: number
    bands?: number
    codebook?: Float32Array
  }
}

/**
 * Splat 属性结构（解压后的单个 splat 数据）
 */
export interface SplatAttributes {
  /** 世界坐标位置 [x, y, z] */
  position: [number, number, number]
  /** 旋转四元数 [w, x, y, z] */
  rotation: [number, number, number, number]
  /** 三轴缩放 [sx, sy, sz] */
  scale: [number, number, number]
  /** RGB 颜色，源自 SH DC 系数 */
  color: [number, number, number]
  /** 不透明度，取值 0~1 */
  opacity: number
  /** 可选高阶球谐系数 */
  sh?: Float32Array
}

/**
 * SogsData 构造参数
 */
export interface SogsDataOptions {
  /** SOGS 元信息 */
  meta: SogsMeta
  /** splat 总数 */
  numSplats: number
  /** 位置低位字节流（RGBA） */
  meansLow: Uint8Array
  /** 位置高位字节流（RGBA） */
  meansHigh: Uint8Array
  /** 四元数字节流（RGBA） */
  quats: Uint8Array
  /** 缩放字节流 */
  scales: Uint8Array
  /** SH DC 颜色字节流 */
  sh0: Uint8Array
  /** SH palette 索引字节流，无高阶 SH 时省略 */
  shLabels?: Uint8Array
  /** SH palette 数据字节流，无高阶 SH 时省略 */
  shCentroids?: Uint8Array
  /** shCentroids 纹理的真实宽度（从 WebP 解码获取） */
  shCentroidsWidth?: number
  /** shCentroids 纹理的真实高度（从 WebP 解码获取） */
  shCentroidsHeight?: number
  /** 是否启用最小内存模式（创建 GPU 纹理后释放 CPU 数据） */
  minimalMemory?: boolean
}

/**
 * SOGS 压缩数据容器
 *
 * PlayCanvas SOGS 格式规范：
 * - 使用 WebP/PNG 纹理存储压缩数据
 * - V1: Min-Max 线性量化
 * - V2: Codebook 量化（更高压缩率）
 *
 * 纹理数据结构：
 * - means_l/means_u: 位置（16-bit，分高低位）
 * - quats: 四元数 + mode（8-bit per channel）
 * - scales: 缩放（8-bit 索引或值）
 * - sh0: DC 颜色（8-bit 索引或值）
 * - sh_labels: SH palette 索引（可选）
 * - sh_centroids: SH palette 数据（可选）
 */
export class SogsData {
  /** SOGS 元信息 */
  readonly meta: SogsMeta
  /** splat 总数 */
  readonly numSplats: number

  /** 位置低位字节流；minimalMemory 模式下上传 GPU 后置为 null */
  meansLow: Uint8Array | null
  /** 位置高位字节流；minimalMemory 模式下上传 GPU 后置为 null */
  meansHigh: Uint8Array | null
  /** 四元数字节流；minimalMemory 模式下上传 GPU 后置为 null */
  quats: Uint8Array | null
  /** 缩放字节流；minimalMemory 模式下上传 GPU 后置为 null */
  scales: Uint8Array | null
  /** SH DC 颜色字节流；minimalMemory 模式下上传 GPU 后置为 null */
  sh0: Uint8Array | null
  /** SH palette 索引字节流，无高阶 SH 时为 null */
  shLabels?: Uint8Array | null
  /** SH palette 数据字节流，无高阶 SH 时为 null */
  shCentroids?: Uint8Array | null

  /** shCentroids 纹理真实宽度，从 WebP 解码获取 */
  readonly shCentroidsWidth: number
  /** shCentroids 纹理真实高度，从 WebP 解码获取 */
  readonly shCentroidsHeight: number

  // GPU 纹理（懒加载）
  private _meansLowTexture: THREE.DataTexture | null = null
  private _meansHighTexture: THREE.DataTexture | null = null
  private _quatsTexture: THREE.DataTexture | null = null
  private _scalesTexture: THREE.DataTexture | null = null
  private _sh0Texture: THREE.DataTexture | null = null

  // 纹理尺寸
  private _textureWidth: number = 0
  private _textureHeight: number = 0

  // 缓存的中心点数组
  private _centers: Float32Array | null = null

  // 资源释放标志
  private _isDisposed: boolean = false

  // 最小内存模式标志
  private _minimalMemory: boolean = false

  constructor(params: SogsDataOptions) {
    this.meta = params.meta
    this.numSplats = params.numSplats
    this.meansLow = params.meansLow
    this.meansHigh = params.meansHigh
    this.quats = params.quats
    this.scales = params.scales
    this.sh0 = params.sh0
    this.shLabels = params.shLabels ?? null
    this.shCentroids = params.shCentroids ?? null

    // 存储 shCentroids 的真实尺寸（从 WebP 解码获取）
    // 如果未提供，则从数据长度推算（回退逻辑）
    if (params.shCentroidsWidth && params.shCentroidsHeight) {
      this.shCentroidsWidth = params.shCentroidsWidth
      this.shCentroidsHeight = params.shCentroidsHeight
    } else if (params.shCentroids) {
      // 回退：从数据长度推算（可能不准确）
      const totalPixels = params.shCentroids.length / 4
      this.shCentroidsWidth = Math.ceil(Math.sqrt(totalPixels))
      this.shCentroidsHeight = Math.ceil(totalPixels / this.shCentroidsWidth)
    } else {
      this.shCentroidsWidth = 0
      this.shCentroidsHeight = 0
    }

    // 计算纹理尺寸
    // 关键修复：基于实际数据长度计算纹理尺寸，而不是从 numSplats 推算
    // 这避免了 WebGL texSubImage2D 错误：ArrayBufferView not big enough for request
    //
    // 原因：
    // - SOGLoader 使用 slice() 提取精确的 numSplats 个像素
    // - 但纹理必须是矩形，ceil(sqrt(numSplats)) * ceil(numSplats/width) 可能 > numSplats
    // - 如果纹理尺寸 > 数据长度，Three.js 上传时会报 texSubImage2D 错误
    const pixelCount = params.meansLow.length / 4
    this._textureWidth = Math.ceil(Math.sqrt(pixelCount))
    this._textureHeight = Math.ceil(pixelCount / this._textureWidth)

    // 如果启用 minimalMemory，立即创建纹理并释放 CPU 数据
    if (params.minimalMemory) {
      this._performMinimalMemorySetup()
    }
  }

  /**
   * 获取纹理宽度
   */
  get textureWidth(): number {
    return this._textureWidth
  }

  /**
   * 获取纹理高度
   */
  get textureHeight(): number {
    return this._textureHeight
  }

  /**
   * 获取 means_l GPU 纹理（懒加载）
   */
  get meansLowTexture(): THREE.DataTexture {
    if (!this._meansLowTexture) {
      if (!this.meansLow) {
        throw new Error('[SogsData] CPU 数据已释放（minimalMemory 模式），无法创建纹理')
      }
      this._meansLowTexture = this.createDataTexture('sogs_means_l', this.meansLow)
    }
    return this._meansLowTexture
  }

  /**
   * 获取 means_u GPU 纹理（懒加载）
   */
  get meansHighTexture(): THREE.DataTexture {
    if (!this._meansHighTexture) {
      if (!this.meansHigh) {
        throw new Error('[SogsData] CPU 数据已释放（minimalMemory 模式），无法创建纹理')
      }
      this._meansHighTexture = this.createDataTexture('sogs_means_u', this.meansHigh)
    }
    return this._meansHighTexture
  }

  /**
   * 获取 quats GPU 纹理（懒加载）
   */
  get quatsTexture(): THREE.DataTexture {
    if (!this._quatsTexture) {
      if (!this.quats) {
        throw new Error('[SogsData] CPU 数据已释放（minimalMemory 模式），无法创建纹理')
      }
      this._quatsTexture = this.createDataTexture('sogs_quats', this.quats)
    }
    return this._quatsTexture
  }

  /**
   * 获取 scales GPU 纹理（懒加载）
   */
  get scalesTexture(): THREE.DataTexture {
    if (!this._scalesTexture) {
      if (!this.scales) {
        throw new Error('[SogsData] CPU 数据已释放（minimalMemory 模式），无法创建纹理')
      }
      this._scalesTexture = this.createDataTexture('sogs_scales', this.scales)
    }
    return this._scalesTexture
  }

  /**
   * 获取 sh0 GPU 纹理（懒加载）
   */
  get sh0Texture(): THREE.DataTexture {
    if (!this._sh0Texture) {
      if (!this.sh0) {
        throw new Error('[SogsData] CPU 数据已释放（minimalMemory 模式），无法创建纹理')
      }
      this._sh0Texture = this.createDataTexture('sogs_sh0', this.sh0)
    }
    return this._sh0Texture
  }

  /**
   * 创建 DataTexture
   *
   * 重要：使用 NoColorSpace 保持原始数据，与 LOD 系统的 SogsParser.loadTexture 一致
   *
   * 关键修复：始终创建正确大小的数组，确保 data.length === width * height * 4
   * Three.js 内部使用 width * height 计算期望大小，如果 data.length 不匹配会导致 WebGL 错误
   */
  private createDataTexture(name: string, data: Uint8Array): THREE.DataTexture {
    // 始终创建正确大小的数组（避免 Three.js texSubImage2D 错误）
    const expectedSize = this._textureWidth * this._textureHeight * 4
    let textureData: Uint8Array

    // 调试日志

    if (data.length === expectedSize) {
      textureData = data
    } else {
      // 创建正确大小的数组并复制数据
      // 这是正常情况：numSplats 通常不等于 width * height
      textureData = new Uint8Array(expectedSize)
      textureData.set(data.subarray(0, Math.min(data.length, expectedSize)))
    }

    const texture = new THREE.DataTexture(
      textureData,
      this._textureWidth,
      this._textureHeight,
      THREE.RGBAFormat,
      THREE.UnsignedByteType
    )
    texture.name = name
    texture.minFilter = THREE.NearestFilter
    texture.magFilter = THREE.NearestFilter
    texture.wrapS = THREE.ClampToEdgeWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    texture.generateMipmaps = false
    // 关键：保持原始数据，不做色彩空间转换（与 SogsParser.loadTexture 一致）
    texture.colorSpace = THREE.NoColorSpace
    texture.needsUpdate = true
    return texture
  }

  /**
   * 创建按需解压迭代器。
   *
   * @returns 复用内部缓冲的迭代器，可按索引随机访问 splat
   */
  createIterator(): SogsIterator {
    if (this._minimalMemory && !this.meansLow) {
      throw new Error('[SogsData] minimalMemory 模式下无法创建迭代器（CPU 数据已释放）')
    }
    return new SogsIterator(this)
  }

  /**
   * 获取单个 splat（按需解压）。
   *
   * @param index - splat 下标，0 ≤ index < numSplats
   * @returns 解压后的 splat 属性
   */
  getSplat(index: number): SplatAttributes {
    const iter = this.createIterator()
    return iter.read(index)
  }

  /**
   * 获取中心点数组（用于排序），优先返回 GPU 生成缓存。
   *
   * @returns 长度 numSplats*3 的数组；若无缓存则走 CPU 解压路径
   */
  getCenters(): Float32Array {
    // 优先返回缓存的 GPU centers
    if (this._centers) {
      const cached = this._centers
      this._centers = null // 只能取一次
      return cached
    }

    // 回退到 CPU 解压
    log.warn('[SogsData] getCenters: 使用 CPU 回退路径，建议先调用 generateCenters()')
    const centers = new Float32Array(this.numSplats * 3)
    const iter = this.createIterator()

    for (let i = 0; i < this.numSplats; i++) {
      const splat = iter.read(i)
      centers[i * 3 + 0] = splat.position[0]
      centers[i * 3 + 1] = splat.position[1]
      centers[i * 3 + 2] = splat.position[2]
    }

    return centers
  }

  /**
   * 生成中心点数组（GPU 加速），失败时自动回退 CPU 路径。
   *
   * 参考 PlayCanvas gsplat-sogs-data.js generateCenters()。
   * Three.js 适配：使用 WebGLRenderTarget + ShaderMaterial 在 GPU 上解压位置数据，
   * 读回数据使用 renderer.readRenderTargetPixels()。
   *
   * @param renderer - 用于执行渲染通道的 WebGLRenderer
   */
  async generateCenters(renderer: THREE.WebGLRenderer): Promise<void> {
    if (this._isDisposed) {
      throw new Error('[SogsData] 资源已释放，无法生成 centers')
    }

    try {
      const width = this._textureWidth
      const height = this._textureHeight

      // 创建 RenderTarget（RGBA32F 用于存储浮点坐标）
      const centersTarget = new THREE.WebGLRenderTarget(width, height, {
        format: THREE.RGBAFormat,
        type: THREE.FloatType,
        minFilter: THREE.NearestFilter,
        magFilter: THREE.NearestFilter,
        generateMipmaps: false,
      })

      // 创建解压 shader
      const material = new THREE.ShaderMaterial({
        glslVersion: THREE.GLSL3,
        uniforms: {
          means_l: { value: this.meansLowTexture },
          means_u: { value: this.meansHighTexture },
          means_mins: { value: new THREE.Vector3(...this.meta.means.mins) },
          means_maxs: { value: new THREE.Vector3(...this.meta.means.maxs) },
        },
        vertexShader: `
          out vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          precision highp float;
          precision highp sampler2D;

          uniform sampler2D means_l;
          uniform sampler2D means_u;
          uniform vec3 means_mins;
          uniform vec3 means_maxs;
          in vec2 vUv;
          out vec4 fragColor;

          vec3 decompressPosition(vec2 uv) {
            vec4 low = texture(means_l, uv);
            vec4 high = texture(means_u, uv);

            // 合并高低位（16-bit）
            float px = ((high.r * 255.0) * 256.0 + low.r * 255.0) / 65535.0;
            float py = ((high.g * 255.0) * 256.0 + low.g * 255.0) / 65535.0;
            float pz = ((high.b * 255.0) * 256.0 + low.b * 255.0) / 65535.0;

            // 反量化到范围
            vec3 n = mix(means_mins, means_maxs, vec3(px, py, pz));

            // 指数反变换（PlayCanvas 特有）
            return sign(n) * (exp(abs(n)) - 1.0);
          }

          void main() {
            vec3 pos = decompressPosition(vUv);
            fragColor = vec4(pos, 1.0);
          }
        `,
      })

      // 渲染到纹理
      const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material)
      const quadScene = new THREE.Scene()
      quadScene.add(quad)
      const quadCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)

      const oldRenderTarget = renderer.getRenderTarget()
      renderer.setRenderTarget(centersTarget)
      renderer.render(quadScene, quadCamera)
      renderer.setRenderTarget(oldRenderTarget)

      // 读回数据
      const buffer = new Float32Array(width * height * 4)
      renderer.readRenderTargetPixels(centersTarget, 0, 0, width, height, buffer)

      // 提取中心点（只取 xyz，忽略 w）
      const centers = new Float32Array(this.numSplats * 3)
      for (let i = 0; i < this.numSplats; i++) {
        centers[i * 3 + 0] = buffer[i * 4 + 0]
        centers[i * 3 + 1] = buffer[i * 4 + 1]
        centers[i * 3 + 2] = buffer[i * 4 + 2]
      }

      this._centers = centers

      // 清理
      centersTarget.dispose()
      material.dispose()
      quad.geometry.dispose()
    } catch (error) {
      log.warn(
        '[SogsData] GPU generateCenters 失败，回退到 CPU: ' +
          (error instanceof Error ? error.message : String(error))
      )

      // CPU fallback
      const centers = new Float32Array(this.numSplats * 3)
      const iter = this.createIterator()

      for (let i = 0; i < this.numSplats; i++) {
        const splat = iter.read(i)
        centers[i * 3 + 0] = splat.position[0]
        centers[i * 3 + 1] = splat.position[1]
        centers[i * 3 + 2] = splat.position[2]
      }

      this._centers = centers
    }
  }

  /**
   * 计算包围盒。
   *
   * @param result - 输出目标，按本地坐标系写入 min/max
   * @returns numSplats 为 0 时返回 false，否则返回 true
   */
  calcAabb(result: THREE.Box3): boolean {
    if (this.numSplats === 0) return false

    let minX = Infinity,
      minY = Infinity,
      minZ = Infinity
    let maxX = -Infinity,
      maxY = -Infinity,
      maxZ = -Infinity

    const iter = this.createIterator()
    for (let i = 0; i < this.numSplats; i++) {
      const splat = iter.read(i)
      const [x, y, z] = splat.position

      if (x < minX) minX = x
      if (y < minY) minY = y
      if (z < minZ) minZ = z
      if (x > maxX) maxX = x
      if (y > maxY) maxY = y
      if (z > maxZ) maxZ = z
    }

    result.min.set(minX, minY, minZ)
    result.max.set(maxX, maxY, maxZ)
    return true
  }

  /**
   * 计算 Morton 码顺序。
   *
   * @returns 长度 numSplats 的索引数组，按 Morton 码升序排列
   */
  calcMortonOrder(): Uint32Array {
    const result = new Uint32Array(this.numSplats)
    const aabb = new THREE.Box3()
    this.calcAabb(aabb)

    const min = aabb.min
    const sizeX = aabb.max.x === min.x ? 0 : 1024 / (aabb.max.x - min.x)
    const sizeY = aabb.max.y === min.y ? 0 : 1024 / (aabb.max.y - min.y)
    const sizeZ = aabb.max.z === min.z ? 0 : 1024 / (aabb.max.z - min.z)

    const codes = new Map<number, number[]>()
    const iter = this.createIterator()

    for (let i = 0; i < this.numSplats; i++) {
      const splat = iter.read(i)
      const [x, y, z] = splat.position

      const ix = Math.min(1023, Math.floor((x - min.x) * sizeX))
      const iy = Math.min(1023, Math.floor((y - min.y) * sizeY))
      const iz = Math.min(1023, Math.floor((z - min.z) * sizeZ))

      const code = this.encodeMorton3D(Math.max(0, ix), Math.max(0, iy), Math.max(0, iz))

      const existing = codes.get(code)
      if (existing) {
        existing.push(i)
      } else {
        codes.set(code, [i])
      }
    }

    const sortedKeys = Array.from(codes.keys()).sort((a, b) => a - b)
    let idx = 0
    for (const key of sortedKeys) {
      const indices = codes.get(key)!
      for (const i of indices) {
        result[idx++] = i
      }
    }

    return result
  }

  private encodeMorton3D(x: number, y: number, z: number): number {
    x = (x | (x << 16)) & 0x030000ff
    x = (x | (x << 8)) & 0x0300f00f
    x = (x | (x << 4)) & 0x030c30c3
    x = (x | (x << 2)) & 0x09249249

    y = (y | (y << 16)) & 0x030000ff
    y = (y | (y << 8)) & 0x0300f00f
    y = (y | (y << 4)) & 0x030c30c3
    y = (y | (y << 2)) & 0x09249249

    z = (z | (z << 16)) & 0x030000ff
    z = (z | (z << 8)) & 0x0300f00f
    z = (z | (z << 4)) & 0x030c30c3
    z = (z | (z << 2)) & 0x09249249

    return x | (y << 1) | (z << 2)
  }

  /**
   * 计算未压缩内存占用。
   *
   * @returns 假设解压到标准浮点属性后的字节数估算值
   */
  getUncompressedMemoryUsage(): number {
    return this.numSplats * 56 // 基础属性
  }

  /**
   * 计算压缩率。
   *
   * @returns 未压缩 / 压缩字节数之比
   */
  getCompressionRatio(): number {
    return this.getUncompressedMemoryUsage() / this.getMemoryUsage()
  }

  /**
   * 获取球谐阶数（根据 palette 纹理推断）
   */
  get shBands(): number {
    const info = this.getShPaletteInfo()
    return info?.shBands ?? 0
  }

  /**
   * 获取 SH palette 信息（宽高 / 阶数 / 每通道系数数）。
   *
   * 优先从 meta.shN.bands 读取（V2 格式明确指定），回退到从纹理宽度推断。
   *
   * @returns palette 描述；无 SH palette 或无法推断时返回 null
   */
  getShPaletteInfo(): { width: number; height: number; coeffs: number; shBands: number } | null {
    if (!this.shCentroids) return null

    const totalPixels = this.shCentroids.length / 4
    let shBands = 0
    let width = 0

    // 优先从 meta.shN.bands 读取（V2 格式明确指定）
    if (this.meta.shN?.bands !== undefined && this.meta.shN.bands > 0) {
      shBands = this.meta.shN.bands
      // 根据 bands 计算期望的宽度：每个 palette entry 占 64 pixels
      // band 1: 3 coeffs → 3 * 64 = 192
      // band 2: 8 coeffs → 8 * 64 = 512
      // band 3: 15 coeffs → 15 * 64 = 960
      const bandsToWidth: Record<number, number> = { 1: 192, 2: 512, 3: 960 }
      width = bandsToWidth[shBands] || 0
      // 验证宽度是否能整除 totalPixels
      if (width === 0 || totalPixels % width !== 0) {
        // 回退：从纹理尺寸推断
        width = 0
      }
    }

    // 回退：从 shCentroids 纹理宽度推断
    if (!width) {
      const widthBands: Record<number, number> = { 192: 1, 512: 2, 960: 3 }
      for (const [candidate, bands] of Object.entries(widthBands)) {
        const candidateWidth = Number(candidate)
        if (totalPixels % candidateWidth === 0) {
          width = candidateWidth
          shBands = bands
          break
        }
      }
    }

    if (!width || !shBands) return null

    const height = totalPixels / width
    const coeffs = shBands === 1 ? 3 : shBands === 2 ? 8 : 15

    return { width, height, coeffs, shBands }
  }

  /**
   * GPU 打包内存（将多个源纹理合并为紧凑格式）
   * 参考 PlayCanvas gsplat-sogs-data.js packGpuMemory()
   *
   * 功能：
   * - 在 GPU 上解压 SOGS 数据并重新打包
   * - 减少纹理采样次数（6-7次 → 2-3次）
   * - 提升 GPU 解压性能 10-20x
   *
   * @param renderer WebGL渲染器
   * @param minimalMemory 是否释放源纹理（默认 false）
   * @returns 打包后的紧凑纹理及尺寸信息
   */
  packGpuMemory(
    renderer: THREE.WebGLRenderer,
    minimalMemory: boolean = false
  ): {
    dataTextureA: THREE.DataTexture
    dataTextureB: THREE.DataTexture
    colorTexture: THREE.DataTexture
    width: number
    height: number
  } | null {
    try {
      const packer = new SogsGpuPacker(renderer)
      const packed = packer.packGpuMemory(this)

      // 清理打包器
      packer.dispose()

      // 如果启用 minimalMemory，使用新方法
      if (minimalMemory) {
        this.enableMinimalMemory()
      }

      return {
        dataTextureA: packed.dataTextureA,
        dataTextureB: packed.dataTextureB,
        colorTexture: packed.colorTexture,
        width: packed.width,
        height: packed.height,
      }
    } catch (error) {
      log.error(
        `[SogsData] GPU 打包失败: ${error instanceof Error ? error.message : String(error)}`
      )
      return null
    }
  }

  /**
   * 从 Uint8Array 创建一组 GPU DataTexture，用于将压缩数据上传到 GPU。
   *
   * @param width - 纹理宽度（像素）
   * @param height - 纹理高度（像素）
   * @returns 包含 meansLow/meansHigh/quats/scales/sh0 五张纹理的对象
   */
  createDataTextures(
    width: number,
    height: number
  ): {
    meansLow: THREE.DataTexture
    meansHigh: THREE.DataTexture
    quats: THREE.DataTexture
    scales: THREE.DataTexture
    sh0: THREE.DataTexture
  } {
    if (this._minimalMemory) {
      throw new Error(
        '[SogsData] minimalMemory 模式下无法调用 createDataTextures（CPU 数据已释放）'
      )
    }

    const createTexture = (data: Uint8Array) => {
      const texture = new THREE.DataTexture(
        data,
        width,
        height,
        THREE.RGBAFormat,
        THREE.UnsignedByteType
      )
      texture.minFilter = THREE.NearestFilter
      texture.magFilter = THREE.NearestFilter
      texture.generateMipmaps = false
      // 关键：保持原始数据，不做色彩空间转换
      texture.colorSpace = THREE.NoColorSpace
      texture.needsUpdate = true
      return texture
    }

    return {
      meansLow: createTexture(this.meansLow!),
      meansHigh: createTexture(this.meansHigh!),
      quats: createTexture(this.quats!),
      scales: createTexture(this.scales!),
      sh0: createTexture(this.sh0!),
    }
  }

  /**
   * 检查是否已释放资源
   */
  get isDisposed(): boolean {
    return this._isDisposed
  }

  /**
   * 释放资源
   */
  dispose(): void {
    if (this._isDisposed) {
      log.warn('[SogsData] 资源已释放，跳过重复释放')
      return
    }

    this._isDisposed = true

    // 释放 GPU 纹理
    if (this._meansLowTexture) {
      this._meansLowTexture.dispose()
      this._meansLowTexture = null
    }
    if (this._meansHighTexture) {
      this._meansHighTexture.dispose()
      this._meansHighTexture = null
    }
    if (this._quatsTexture) {
      this._quatsTexture.dispose()
      this._quatsTexture = null
    }
    if (this._scalesTexture) {
      this._scalesTexture.dispose()
      this._scalesTexture = null
    }
    if (this._sh0Texture) {
      this._sh0Texture.dispose()
      this._sh0Texture = null
    }

    // 清空中心点缓存
    this._centers = null

    // 注意：CPU 数据（meansLow, meansHigh 等）是 readonly，
    // 由垃圾回收器自动回收，这里不需要手动释放
  }

  /**
   * 启用最小内存模式
   * 创建所有 GPU 纹理后释放 CPU 数据
   */
  enableMinimalMemory(): void {
    if (this._minimalMemory) {
      log.warn('[SogsData] 已处于 minimalMemory 模式')
      return
    }

    this._performMinimalMemorySetup()
  }

  /**
   * 执行最小内存模式设置（内部方法）
   */
  private _performMinimalMemorySetup(): void {
    this._minimalMemory = true

    // 强制创建所有纹理
    this._meansLowTexture = this.createDataTexture('sogs_means_l', this.meansLow!)
    this._meansHighTexture = this.createDataTexture('sogs_means_u', this.meansHigh!)
    this._quatsTexture = this.createDataTexture('sogs_quats', this.quats!)
    this._scalesTexture = this.createDataTexture('sogs_scales', this.scales!)
    this._sh0Texture = this.createDataTexture('sogs_sh0', this.sh0!)

    // 释放 CPU 数据
    this.meansLow = null
    this.meansHigh = null
    this.quats = null
    this.scales = null
    this.sh0 = null
    this.shLabels = null
    this.shCentroids = null
  }

  /**
   * 计算内存占用。
   *
   * @returns minimalMemory 模式估算 GPU 纹理字节；否则返回 CPU 数据字节总和
   */
  getMemoryUsage(): number {
    if (this._minimalMemory) {
      // minimalMemory 模式下只计算 GPU 纹理占用（估算）
      const texelCount = this._textureWidth * this._textureHeight
      return texelCount * 4 * 5 // 5 个 RGBA8 纹理
    }

    let total = 0
    if (this.meansLow) total += this.meansLow.byteLength
    if (this.meansHigh) total += this.meansHigh.byteLength
    if (this.quats) total += this.quats.byteLength
    if (this.scales) total += this.scales.byteLength
    if (this.sh0) total += this.sh0.byteLength
    if (this.shLabels) total += this.shLabels.byteLength
    if (this.shCentroids) total += this.shCentroids.byteLength
    return total
  }

  /**
   * 获取指定索引 splat 的中心位置
   * @param index - splat 索引
   * @param target - 可选的目标 Vector3，用于存储结果以避免内存分配
   * @returns 中心位置向量
   */
  getSplatCenter(index: number, target?: THREE.Vector3): THREE.Vector3 {
    if (index < 0 || index >= this.numSplats) {
      throw new Error(`[SogsData] 无效的 splat 索引: ${index} (总数: ${this.numSplats})`)
    }

    const result = target || new THREE.Vector3()
    const splat = this.getSplat(index)
    result.set(splat.position[0], splat.position[1], splat.position[2])
    return result
  }

  /**
   * 获取指定索引 splat 的缩放值（从 log 空间转换）
   * @param index - splat 索引
   * @param target - 可选的目标 Vector3，用于存储结果以避免内存分配
   * @returns 缩放向量（已转换为实际空间）
   */
  getSplatScale(index: number, target?: THREE.Vector3): THREE.Vector3 {
    if (index < 0 || index >= this.numSplats) {
      throw new Error(`[SogsData] 无效的 splat 索引: ${index} (总数: ${this.numSplats})`)
    }

    const result = target || new THREE.Vector3()
    const splat = this.getSplat(index)
    // 从 log 空间转换到实际空间
    result.set(Math.exp(splat.scale[0]), Math.exp(splat.scale[1]), Math.exp(splat.scale[2]))
    return result
  }

  /**
   * 获取指定索引 splat 的旋转四元数
   * @param index - splat 索引
   * @param target - 可选的目标 Quaternion，用于存储结果以避免内存分配
   * @returns 旋转四元数（归一化）
   */
  getSplatRotation(index: number, target?: THREE.Quaternion): THREE.Quaternion {
    if (index < 0 || index >= this.numSplats) {
      throw new Error(`[SogsData] 无效的 splat 索引: ${index} (总数: ${this.numSplats})`)
    }

    const result = target || new THREE.Quaternion()
    const splat = this.getSplat(index)
    // splat.rotation 格式: [w, x, y, z]
    // THREE.Quaternion.set() 格式: (x, y, z, w)
    result.set(splat.rotation[1], splat.rotation[2], splat.rotation[3], splat.rotation[0])
    // 确保归一化
    result.normalize()
    return result
  }
}

/**
 * SOGS 数据迭代器（按需解压）
 */
export class SogsIterator {
  private readonly data: SogsData

  constructor(data: SogsData) {
    if (!data.meansLow || !data.meansHigh || !data.quats || !data.scales || !data.sh0) {
      throw new Error('[SogsIterator] CPU 数据已释放，无法创建迭代器')
    }
    this.data = data
  }

  /**
   * 解压单个 splat。
   *
   * @param index - splat 下标，0 ≤ index < numSplats
   * @returns 内部复用对象，跨次调用会被覆写
   */
  read(index: number): SplatAttributes {
    const pixelOffset = index * 4 // RGBA

    // 确保数据存在（TypeScript null check）
    const meansLow = this.data.meansLow!
    const meansHigh = this.data.meansHigh!
    const quats = this.data.quats!
    const scales = this.data.scales!
    const sh0 = this.data.sh0!

    // 1. 解码位置（16-bit 精度，指数反变换）
    const lowR = meansLow[pixelOffset]
    const lowG = meansLow[pixelOffset + 1]
    const lowB = meansLow[pixelOffset + 2]
    const highR = meansHigh[pixelOffset]
    const highG = meansHigh[pixelOffset + 1]
    const highB = meansHigh[pixelOffset + 2]

    // 合并高低位（16-bit）
    const px16 = ((highR << 8) | lowR) / 65535
    const py16 = ((highG << 8) | lowG) / 65535
    const pz16 = ((highB << 8) | lowB) / 65535

    // 反量化到范围
    const mins = this.data.meta.means.mins
    const maxs = this.data.meta.means.maxs
    const nx = this.lerp(mins[0], maxs[0], px16)
    const ny = this.lerp(mins[1], maxs[1], py16)
    const nz = this.lerp(mins[2], maxs[2], pz16)

    // 指数反变换（PlayCanvas 特有）
    const x = Math.sign(nx) * (Math.exp(Math.abs(nx)) - 1)
    const y = Math.sign(ny) * (Math.exp(Math.abs(ny)) - 1)
    const z = Math.sign(nz) * (Math.exp(Math.abs(nz)) - 1)

    // 2. 解码四元数（SOGS 压缩格式）
    // PlayCanvas 格式：RGB 存储 3 个分量，A 存储 mode (252-255)
    // mode 指示哪个四元数分量被省略（最大的那个）
    const a = (quats[pixelOffset] / 255 - 0.5) * Math.SQRT2
    const b = (quats[pixelOffset + 1] / 255 - 0.5) * Math.SQRT2
    const c = (quats[pixelOffset + 2] / 255 - 0.5) * Math.SQRT2
    const d = Math.sqrt(Math.max(0, 1 - (a * a + b * b + c * c)))
    const mode = quats[pixelOffset + 3] - 252

    // 根据 mode 重排四元数分量
    // PlayCanvas 使用 (x, y, z, w) 顺序，ThreeGS 返回 (w, x, y, z) 顺序
    let rot_w: number, rot_x: number, rot_y: number, rot_z: number
    switch (mode) {
      case 0: // w 最大
        rot_x = a
        rot_y = b
        rot_z = c
        rot_w = d
        break
      case 1: // x 最大
        rot_x = d
        rot_y = b
        rot_z = c
        rot_w = a
        break
      case 2: // y 最大
        rot_x = b
        rot_y = d
        rot_z = c
        rot_w = a
        break
      case 3: // z 最大
        rot_x = b
        rot_y = c
        rot_z = d
        rot_w = a
        break
      default: {
        // Fallback: 直接归一化（兼容旧数据）
        log.warn(`[SogsIterator] 未知的四元数 mode: ${mode + 252}, fallback 到归一化`)
        rot_x = a
        rot_y = b
        rot_z = c
        rot_w = d
        const qLen = Math.sqrt(rot_x * rot_x + rot_y * rot_y + rot_z * rot_z + rot_w * rot_w)
        rot_w /= qLen
        rot_x /= qLen
        rot_y /= qLen
        rot_z /= qLen
      }
    }

    // 3. 解码缩放（V1 vs V2）
    let scale_0: number, scale_1: number, scale_2: number

    if (this.data.meta.version === 2 && this.data.meta.scales.codebook) {
      // V2: Codebook 量化
      const codebook = this.data.meta.scales.codebook
      scale_0 = codebook[scales[pixelOffset]] ?? -10
      scale_1 = codebook[scales[pixelOffset + 1]] ?? -10
      scale_2 = codebook[scales[pixelOffset + 2]] ?? -10
    } else {
      // V1: Min-Max 线性插值
      const mins = this.data.meta.scales.mins!
      const maxs = this.data.meta.scales.maxs!
      scale_0 = this.lerp(mins[0], maxs[0], scales[pixelOffset] / 255)
      scale_1 = this.lerp(mins[1], maxs[1], scales[pixelOffset + 1] / 255)
      scale_2 = this.lerp(mins[2], maxs[2], scales[pixelOffset + 2] / 255)
    }

    // 4. 解码 SH0（DC 颜色，V1 vs V2）
    // 参考 PlayCanvas gsplat-sogs-data.js 第 112-136 行
    // V2 和 V1 的 codebook/mins-maxs 都直接存储 SH DC 系数
    let f_dc_0: number, f_dc_1: number, f_dc_2: number, opacity: number

    if (this.data.meta.version === 2 && this.data.meta.sh0.codebook) {
      // V2: Codebook 直接存储 SH DC 系数
      const codebook = this.data.meta.sh0.codebook
      f_dc_0 = codebook[sh0[pixelOffset]] ?? 0
      f_dc_1 = codebook[sh0[pixelOffset + 1]] ?? 0
      f_dc_2 = codebook[sh0[pixelOffset + 2]] ?? 0

      // V2: alpha 通道是直接的不透明度 [0, 1]
      // PlayCanvas gsplat-sogs-data.js 第 117-123 行：直接使用 alpha，不转为 logit 空间
      opacity = sh0[pixelOffset + 3] / 255
    } else {
      // V1: mins/maxs 直接存储 SH DC 系数的范围
      const mins = this.data.meta.sh0.mins!
      const maxs = this.data.meta.sh0.maxs!
      f_dc_0 = this.lerp(mins[0], maxs[0], sh0[pixelOffset] / 255)
      f_dc_1 = this.lerp(mins[1], maxs[1], sh0[pixelOffset + 1] / 255)
      f_dc_2 = this.lerp(mins[2], maxs[2], sh0[pixelOffset + 2] / 255)
      // V1: mins[3]/maxs[3] 存储的是 logit 空间的不透明度范围
      opacity = this.lerp(mins[3], maxs[3], sh0[pixelOffset + 3] / 255)
    }

    // 5. 解码高阶 SH（如果存在 palette）
    let shCoeffs: Float32Array | undefined
    if (this.data.shLabels && this.data.shCentroids && this.data.meta.shN) {
      const paletteInfo = this.data.getShPaletteInfo()
      const { shN } = this.data.meta
      if (paletteInfo && shN) {
        const { width, coeffs, shBands } = paletteInfo
        const label = this.data.shLabels[pixelOffset] + (this.data.shLabels[pixelOffset + 1] << 8)
        const u = (label % 64) * coeffs
        const v = Math.floor(label / 64)

        shCoeffs = new Float32Array(45) // 最多 3 阶（15 系数）* 3 通道

        for (let j = 0; j < 3; ++j) {
          for (let k = 0; k < coeffs; ++k) {
            const idx = (u + k) * 4 + j + v * width * 4
            const encoded = this.data.shCentroids[idx]
            let value = 0
            if (shN.codebook) {
              value = shN.codebook[encoded] ?? 0
            } else if (shN.mins !== undefined && shN.maxs !== undefined) {
              value = this.lerp(shN.mins, shN.maxs, encoded / 255)
            }
            shCoeffs[j * 15 + k] = value
          }
        }

        // 对齐 SH 阶数（超过的部分保持 0）
        if (shBands < 3) {
          shCoeffs.fill(0, shBands === 1 ? 9 : 24)
        }
      }
    }

    return {
      position: [x, y, z],
      rotation: [rot_w, rot_x, rot_y, rot_z],
      scale: [scale_0, scale_1, scale_2],
      color: [f_dc_0, f_dc_1, f_dc_2],
      opacity,
      sh: shCoeffs,
    }
  }

  private lerp(a: number, b: number, t: number): number {
    return a * (1 - t) + b * t
  }
}
